import { Children } from 'react';
import { components } from 'react-select';
import CheckmarkIcon from '@common/components/icons/CheckmarkIcon';
import CloseIconSmall from '@common/components/icons/CloseIconSmall';
import * as styles from './index.module.css';

// Custom DropdownIndicator with filled triangle arrow
export function DropdownIndicator(props) {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.9375 0.0678711L4.84984 5.27257C5.4631 6.08841 6.68742 6.08841 7.30068 5.27257L11.213 0.0678711H0.9375Z"
          fill="currentColor"
        />
      </svg>
    </components.DropdownIndicator>
  );
}

// Custom Option component with checkbox
export function Option(props) {
  const { isSelected, label } = props;
  return (
    <components.Option {...props}>
      <div className={styles['option-content']}>
        <span className={styles['checkbox-container']}>
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            className={styles['custom-checkbox']}
          />
          {isSelected && (
            <span className={styles['checkbox-icon-container']}>
              <CheckmarkIcon />
            </span>
          )}
        </span>
        <span className={styles['option-label']}>{label}</span>
      </div>
    </components.Option>
  );
}

// Custom MultiValueRemove component for the remove button
export function MultiValueRemove(props) {
  return (
    <components.MultiValueRemove {...props}>
      <CloseIconSmall />
    </components.MultiValueRemove>
  );
}

// Custom MenuList with Select All option
export function MenuList(props) {
  const { children, selectProps } = props;
  const { value, onChange } = selectProps;

  // "Select all" must honour the typed search term. react-select narrows the
  // *displayed* options by the search input but leaves selectProps.options as
  // the full, unfiltered list, so we read the visible options from the rendered
  // children instead (MON-17750). This assumes selected options stay rendered —
  // true while every consumer keeps the default hideSelectedOptions={false}; if
  // one ever sets it true, selected options drop out of `children` and the
  // checkbox state below would misreport.
  const visibleOptions = Children.toArray(children)
    .map((child) => child?.props?.data)
    .filter(Boolean);

  const selectedValues = new Set((value || []).map((option) => option.value));
  const allSelected =
    visibleOptions.length > 0 &&
    visibleOptions.every((option) => selectedValues.has(option.value));

  // Select exactly the visible options, or clear when they are all already
  // selected. We don't try to preserve selections made under a previous search
  // term: AsyncSelectMulti refetches its options on every search, so those
  // earlier selections aren't present in selectProps.value to merge back in.
  const handleSelectAll = () => {
    onChange(allSelected ? [] : visibleOptions);
  };

  return (
    <components.MenuList {...props}>
      {visibleOptions.length > 0 && (
        <>
          <div
            className={styles['select-all-option']}
            onClick={handleSelectAll}
            role="option"
            aria-selected={allSelected}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleSelectAll()}
          >
            <span className={styles['checkbox-container']}>
              <input
                type="checkbox"
                checked={allSelected}
                readOnly
                className={styles['custom-checkbox']}
              />
              {allSelected && (
                <span className={styles['checkbox-icon-container']}>
                  <CheckmarkIcon />
                </span>
              )}
            </span>
            <span className={styles['option-label']}>Select all</span>
          </div>
          <div className={styles.divider} />
        </>
      )}
      {children}
    </components.MenuList>
  );
}
